import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { apiService } from '../services/api.service';
import { useTenant } from '../context/TenantContext';

const Logo = ({ variant = 'default', size = 'medium' }) => {
  // Colors for fallback logo
  const primaryColor = '#1A2942';  // Dark blue - matching our nav
  const accentColor = '#00A3E0';   // River blue
  
  const [logoUrl, setLogoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { tenantAlias } = useTenant();
  
  // Size mapping
  const sizeMap = {
    small: { height: '30px', width: '30px', textVariant: 'h6' },
    medium: { height: '40px', width: '40px', textVariant: 'h5' },
    large: { height: '50px', width: '50px', textVariant: 'h4' }
  };
  
  const dimensions = sizeMap[size] || sizeMap.medium;
  
  // Fetch logo from API
  useEffect(() => {
    if (!tenantAlias) {
      setLoading(false);
      return;
    }
    
    const logoType = variant === 'symbol' ? 'symbol' : 'primary';
    
    const fetchLogo = async () => {
      try {
        setLoading(true);
        
        // Get logo info first
        const response = await apiService.get(`/ux/logos/${tenantAlias}/info?logo_type=${logoType}`);
        
        if (response && response.data_uri) {
          setLogoUrl(response.data_uri);
          setError(null);
        } else {
          setLogoUrl(null);
        }
      } catch (err) {
        console.error('Failed to fetch logo:', err);
        setError(err);
        setLogoUrl(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchLogo();
  }, [tenantAlias, variant]);
  
  // Show loading indicator while fetching logo
  if (loading) {
    return <CircularProgress size={dimensions.height} color="primary" />;
  }
  
  // If we have a logo URL, display the image
  if (logoUrl) {
    return (
      <Box 
        component="img"
        src={logoUrl}
        alt="Company Logo"
        sx={{ 
          height: dimensions.height,
          maxWidth: variant === 'default' ? '200px' : dimensions.width,
          objectFit: 'contain'
        }}
      />
    );
  }
  
  // Fallback to SVG logo if no custom logo is available
  if (variant === 'symbol') {
    return (
      <Box 
        component="svg" 
        viewBox="0 0 50 50" 
        sx={{ 
          height: dimensions.height,
          width: dimensions.width
        }}
      >
        <path
          d="M10 25 Q 25 15, 40 25 Q 25 35, 10 25"
          fill="none"
          stroke={accentColor}
          strokeWidth="3"
        />
        <path
          d="M15 25 Q 25 18, 35 25 Q 25 32, 15 25"
          fill="none"
          stroke={primaryColor}
          strokeWidth="2"
        />
      </Box>
    );
  }

  // Default logo with text
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box 
        component="svg" 
        viewBox="0 0 50 50" 
        sx={{ 
          height: dimensions.height,
          width: dimensions.width
        }}
      >
        <path
          d="M10 25 Q 25 15, 40 25 Q 25 35, 10 25"
          fill="none"
          stroke={accentColor}
          strokeWidth="3"
        />
        <path
          d="M15 25 Q 25 18, 35 25 Q 25 32, 15 25"
          fill="none"
          stroke={primaryColor}
          strokeWidth="2"
        />
      </Box>
      <Typography 
        variant={dimensions.textVariant} 
        component="span" 
        sx={{ 
          fontWeight: 700,
          color: primaryColor,
          letterSpacing: '-0.5px'
        }}
      >
        {tenantAlias?.split('_')[0] || 'nile'}
        <Typography 
          component="span" 
          sx={{ 
            color: accentColor,
            fontWeight: 700 
          }}
        >
          .com
        </Typography>
      </Typography>
    </Box>
  );
};

export default Logo; 